// client/src/coms/presence/PresenceUi.js
// Shared presence pieces: the status dot (green Online, amber moon Idle / AFK, red Do Not Disturb, hollow grey
// Offline / Invisible), the AFK / DND label next to a name, and the duration control (number + unit + "until I turn
// it off") used by Appear offline, Idle / AFK and Do Not Disturb.
import React, { useId } from 'react';
import { UNITS, durationError } from './durations';
import './presence.css';

export const STATUS_META = {
  online: { label: 'Online', short: 'online' },
  idle: { label: 'Idle / AFK', short: 'AFK' },
  dnd: { label: 'Do Not Disturb', short: 'do not disturb' },
  invisible: { label: 'Appear offline', short: 'invisible' },
  offline: { label: 'Offline', short: 'offline' },
};

export function statusTitle(status, customText) {
  const meta = STATUS_META[status] || STATUS_META.offline;
  const base = status === 'invisible' ? 'Appear offline (others see you as offline)' : meta.label;
  return customText ? `${base}: ${customText}` : base;
}

export function StatusDot({ status = 'offline', size = 10, title, className = '', ring = false }) {
  const s = STATUS_META[status] ? status : 'offline';
  return (
    <span
      className={`vx-pres-dot is-${s}${ring ? ' has-ring' : ''}${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={title || STATUS_META[s].label}
      title={title || STATUS_META[s].label}
      data-presence={s}
    />
  );
}

// Small inline marker after a name: a moon and "AFK", or "DND". Nothing for Online / Offline.
export function StatusTag({ status }) {
  if (status === 'idle') {
    return (
      <span className="vx-pres-tag is-idle" title="Idle / AFK" data-testid="presence-afk">
        <span aria-hidden="true">🌙</span> AFK
      </span>
    );
  }
  if (status === 'dnd') {
    return (
      <span className="vx-pres-tag is-dnd" title="Do Not Disturb">
        DND
      </span>
    );
  }
  return null;
}

export function DurationPicker({ value, onChange, disabled, allowForever = true, testId = 'presence-duration' }) {
  const id = useId();
  const err = durationError(value);
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="vx-pres-duration" data-testid={testId}>
      <div className="vx-pres-duration-row">
        <label className="vx-pres-sr" htmlFor={`${id}-n`}>
          How long
        </label>
        <input
          id={`${id}-n`}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          className="vx-pres-number"
          value={value.amount}
          disabled={disabled || value.forever}
          aria-invalid={!!err}
          onChange={(e) => set({ amount: e.target.value.replace(/[^\d]/g, '') })}
          data-testid={`${testId}-amount`}
        />
        <label className="vx-pres-sr" htmlFor={`${id}-u`}>
          Unit
        </label>
        <select id={`${id}-u`} className="vx-pres-unit" value={value.unit} disabled={disabled || value.forever} onChange={(e) => set({ unit: e.target.value })} data-testid={`${testId}-unit`}>
          {UNITS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </select>
        {allowForever ? (
          <label className="vx-pres-forever">
            <input type="checkbox" checked={!!value.forever} disabled={disabled} onChange={(e) => set({ forever: e.target.checked })} data-testid={`${testId}-forever`} />
            Until I turn it off
          </label>
        ) : null}
      </div>
      {err ? (
        <div className="vx-pres-error" role="alert">
          {err}
        </div>
      ) : null}
    </div>
  );
}
